import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, ButtonGroup, Row, Table, Input, Label, Col } from "reactstrap";
import ExportJsonExcel from "js-export-excel";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import axios from "axios";
import moment from "moment";
import { TableHeader, Pagination, Search } from "../../../DataTable";
import useFullPageLoader from "../../../hooks/useFullPageLoader";

// Orden natural de tallas (las que no esten aqui van al final, numericas en orden)
const ORDEN_TALLAS = ["XXCH", "XCH", "ECH", "CH", "M", "G", "XG", "EG", "XXG", "1X", "2X", "3X"];
const ordenTalla = (t) => {
  const i = ORDEN_TALLAS.indexOf(String(t).toUpperCase());
  if (i >= 0) return i;
  const n = parseFloat(t);
  return isNaN(n) ? 1000 : 100 + n;
};

// Columnas de cada vista
const COLUMNAS = {
  detalle: [
    { name: "Fecha", field: "fecha" },
    { name: "Nota", field: "idSurtido" },
    { name: "Pedido", field: "idPedido" },
    { name: "Cliente", field: "cliente" },
    { name: "Modelo", field: "codigo" },
    { name: "Descripcion", field: "descripcion" },
    { name: "Color", field: "color" },
    { name: "Talla", field: "talla" },
    { name: "Cantidad", field: "cantidad", num: true },
  ],
  // Un renglon por articulo en cada nota (suma todos sus colores y tallas)
  articulo: [
    { name: "Fecha", field: "fecha" },
    { name: "Nota", field: "idSurtido" },
    { name: "Pedido", field: "idPedido" },
    { name: "Cliente", field: "cliente" },
    { name: "Modelo", field: "codigo" },
    { name: "Descripcion", field: "descripcion" },
    { name: "Cantidad", field: "cantidad", num: true },
  ],
};

// Reporte de lo que se surtio: fecha, cliente, modelo, color, talla y cantidad
function ListadoArticulosSurtidos() {
  const { user } = useContext(AuthContext);
  const URL_ARTICULOS_SURTIDOS = process.env.REACT_APP_URL_ARTICULOS_SURTIDOS;

  const hoy = moment().format("YYYY-MM-DD");
  const haceUnMes = moment().subtract(30, "days").format("YYYY-MM-DD");

  const [comments, setComments] = useState([]);
  const [selectedFechaInicio, setSelectedFechaInicio] = useState(haceUnMes);
  const [selectedFechaFin, setSelectedFechaFin] = useState(hoy);
  const [selectedCliente, setSelectedCliente] = useState("");
  const [selectedModelo, setSelectedModelo] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedTalla, setSelectedTalla] = useState("");
  const [agrupar, setAgrupar] = useState("detalle");

  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });
  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    if (selectedFechaInicio === "" || selectedFechaFin === "") return;
    showLoader();
    axios
      .get(
        `${URL_ARTICULOS_SURTIDOS}Reporte/${selectedFechaInicio}/${selectedFechaFin}`,
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then((response) => {
        const data = response.data.map((a, i) => ({
          ...a,
          id: i,
          idCliente: String(a.idCliente || ""),
          idArticulo: String(a.idArticulo || ""),
          idColor: String(a.idColor || ""),
          idTalla: String(a.idTalla || ""),
          modelo: a.codigo + " " + a.descripcion,
        }));
        setComments(data);
        setCurrentPage(1);
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();
      });
  }, [selectedFechaInicio, selectedFechaFin]);

  // Opciones de los filtros, sacadas de lo que se surtio en el rango
  const unicos = (campoId, campoNombre) => {
    const m = {};
    comments.forEach((a) => {
      m[a[campoId]] = a[campoNombre];
    });
    return Object.keys(m)
      .map((id) => ({ id, nombre: m[id] }))
      .sort((a, b) => (a.nombre > b.nombre ? 1 : -1));
  };
  const clientes = useMemo(() => unicos("idCliente", "cliente"), [comments]);
  const modelos = useMemo(() => unicos("idArticulo", "modelo"), [comments]);
  const colores = useMemo(() => unicos("idColor", "color"), [comments]);
  const tallas = useMemo(
    () =>
      unicos("idTalla", "talla").sort(
        (a, b) => ordenTalla(a.nombre) - ordenTalla(b.nombre)
      ),
    [comments]
  );

  // Todo lo filtrado (antes de agrupar)
  const filtrados = useMemo(() => {
    let c = comments;
    if (selectedCliente) c = c.filter((e) => e.idCliente === selectedCliente);
    if (selectedModelo) c = c.filter((e) => e.idArticulo === selectedModelo);
    if (selectedColor) c = c.filter((e) => e.idColor === selectedColor);
    if (selectedTalla) c = c.filter((e) => e.idTalla === selectedTalla);
    if (search) {
      const s = search.toLowerCase();
      c = c.filter(
        (e) =>
          e.cliente.toLowerCase().includes(s) ||
          e.codigo.toLowerCase().includes(s) ||
          e.descripcion.toLowerCase().includes(s) ||
          e.color.toLowerCase().includes(s) ||
          e.talla.toLowerCase().includes(s) ||
          String(e.idSurtido).includes(s) ||
          String(e.idPedido).includes(s)
      );
    }
    return c;
  }, [
    comments,
    selectedCliente,
    selectedModelo,
    selectedColor,
    selectedTalla,
    search,
  ]);

  // Vista: detalle, o un renglon por articulo en cada nota (sin color ni talla)
  const vista = useMemo(() => {
    if (agrupar === "detalle") return filtrados;
    const g = {};
    filtrados.forEach((a) => {
      const k = a.idSurtido + "|" + a.idArticulo;
      if (!g[k]) {
        g[k] = {
          id: k,
          fecha: a.fecha,
          idSurtido: a.idSurtido,
          idPedido: a.idPedido,
          cliente: a.cliente,
          codigo: a.codigo,
          descripcion: a.descripcion,
          cantidad: 0,
        };
      }
      g[k].cantidad += a.cantidad;
    });
    // Mismo orden que el detalle: fecha y nota mas recientes primero
    return Object.values(g).sort(
      (a, b) =>
        (a.fecha === b.fecha ? 0 : a.fecha < b.fecha ? 1 : -1) ||
        b.idSurtido - a.idSurtido ||
        a.codigo.localeCompare(b.codigo)
    );
  }, [filtrados, agrupar]);

  const columnas = COLUMNAS[agrupar];
  const totalCantidad = filtrados.reduce((t, a) => t + a.cantidad, 0);
  const headers = columnas.map((c) => ({
    name: c.name,
    field: c.field,
    sortable: true,
  }));

  const commentsData = useMemo(() => {
    let computed = [...vista];
    setTotalItems(computed.length);
    if (sorting.field) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      computed.sort((a, b) => {
        const x = a[sorting.field];
        const y = b[sorting.field];
        if (sorting.field === "talla") {
          return reversed * (ordenTalla(x) - ordenTalla(y));
        }
        if (typeof x === "number" && typeof y === "number") {
          return reversed * (x - y);
        }
        return reversed * String(x).localeCompare(String(y));
      });
    }
    return computed.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [vista, currentPage, sorting]);

  const formato = (c, v) =>
    c.num ? new Intl.NumberFormat("en-US").format(v) : v;

  const nombreVista = agrupar === "articulo" ? " (por Articulo)" : "";

  function excel() {
    const dataExcel = vista.map((a) => {
      const r = {};
      columnas.forEach((c) => {
        r[c.name] = a[c.field];
      });
      return r;
    });
    const nombres = columnas.map((c) => c.name);
    var option = {};
    option.fileName = `ArticulosSurtidos-${selectedFechaInicio}-${selectedFechaFin}`;
    option.datas = [
      {
        sheetData: dataExcel,
        sheetName: "Articulos Surtidos",
        sheetFilter: nombres,
        sheetHeader: nombres,
      },
    ];
    var toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  }

  function PDFTabla() {
    const data = vista.map((a) => columnas.map((c) => formato(c, a[c.field])));
    const doc = new jsPDF("landscape");
    var img = new Image();
    img.src = process.env.PUBLIC_URL + "Logo-Lynqro-PDF.png";
    doc.addImage(img, "png", 230, 30, 45, 15);
    var img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 120, 15, 60, 25);
    doc.text(`Articulos Surtidos${nombreVista}`, 20, 35);
    doc.text(`${selectedFechaInicio} - ${selectedFechaFin}`, 20, 42);
    const pie = columnas.map(() => "");
    pie[pie.length - 2] = "Total";
    pie[pie.length - 1] = new Intl.NumberFormat("en-US").format(totalCantidad);
    doc.autoTable({
      head: [columnas.map((c) => c.name)],
      body: data,
      startY: 50,
      foot: [pie],
      showFoot: "lastPage",
    });
    doc.save(`ArticulosSurtidos-${selectedFechaInicio}-${selectedFechaFin}.pdf`);
  }

  const filtro = (label, value, setter, opciones) => (
    <Col md={2}>
      <Label className="mr-sm-2">{label}</Label>
      <Input
        bsSize="sm"
        type="select"
        value={value}
        onChange={(e) => {
          setter(e.target.value);
          setCurrentPage(1);
        }}
      >
        <option value="">Selecciona</option>
        {opciones.map((o) => (
          <option value={o.id} key={o.id}>
            {o.nombre}
          </option>
        ))}
      </Input>
    </Col>
  );

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_surtidos ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuVentas"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button
                  size="sm"
                  className="btn"
                  color="danger"
                  onClick={PDFTabla}
                  disabled={vista.length === 0}
                >
                  PDF <i class="far fa-file-pdf"></i>
                </Button>
                <Button
                  size="sm"
                  className="btn"
                  color="primary"
                  onClick={excel}
                  disabled={vista.length === 0}
                >
                  Excel <i class="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
          <h3 align="center">Reporte Articulos Surtidos</h3>
          <div className="row">
            <div className="col-md-8">
              <Pagination
                total={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
            <div className="col-md-4 d-flex flex-row-reverse">
              <Search
                onSearch={(value) => {
                  setSearch(value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
          <Row>
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                required
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                required
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            {filtro("Cliente", selectedCliente, setSelectedCliente, clientes)}
            {filtro("Modelo", selectedModelo, setSelectedModelo, modelos)}
            {filtro("Color", selectedColor, setSelectedColor, colores)}
            {filtro("Talla", selectedTalla, setSelectedTalla, tallas)}
          </Row>
          <br />
          <Row>
            <Col md={2}>
              <Label className="mr-sm-2">Agrupar por</Label>
              <Input
                bsSize="sm"
                type="select"
                value={agrupar}
                onChange={(e) => {
                  setAgrupar(e.target.value);
                  setSorting({ field: "", order: "" });
                  setCurrentPage(1);
                }}
              >
                <option value="detalle">Detalle (con color y talla)</option>
                <option value="articulo">Articulo (sin color ni talla)</option>
              </Input>
            </Col>
            <Col md={10}>
              <br />
              <h5>
                Total: {new Intl.NumberFormat("en-US").format(totalCantidad)}{" "}
                piezas {" | "}
                {new Intl.NumberFormat("en-US").format(vista.length)} renglones
              </h5>
            </Col>
          </Row>
          <Table size="sm" striped borderless className="table-responsive-xl">
            <TableHeader
              key={agrupar}
              headers={headers}
              onSorting={(field, order) => setSorting({ field, order })}
            />
            <tbody>
              {commentsData.map((a) => (
                <tr key={a.id}>
                  {columnas.map((c) => (
                    <td key={c.field}>{formato(c, a[c.field])}</td>
                  ))}
                </tr>
              ))}
              <tr>
                {columnas.map((c, i) => (
                  <td key={c.field} className="negrita">
                    {i === columnas.length - 2
                      ? "TOTAL"
                      : i === columnas.length - 1
                      ? new Intl.NumberFormat("en-US").format(totalCantidad)
                      : ""}
                  </td>
                ))}
              </tr>
            </tbody>
          </Table>
          <div className="col-md-6">
            <Pagination
              total={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      ) : undefined}
      {loader}
    </>
  );
}

export default ListadoArticulosSurtidos;
